import { type FC, type MouseEvent, useMemo } from 'react';
import {
  SizedCell,
  SortableHeadCell,
  TextEllipsisWithTooltip,
} from '__components/Kizen/Table';
import { useTranslation } from 'react-i18next';
import DateTimeInputInline from '__components/Inputs/inline/DateTimeInput';
import ActionCell from '__components/Tables/ActionCell';
import { KizenTypography } from '__app/typography';
import { ONLINE_SOURCES_TABLE_ACTIONS } from 'pages/KnowledgeSettings/constants';
import type { OnlineSourceListItem } from '@kizen/api/knowledge';
import { getExecutionStatusConfig } from 'pages/KnowledgeSettings/helpers';
import { useHistory, useParams } from 'react-router-dom';
import { getCanStartCrawl } from './helpers';

const EMPTY_VALUE = '—';

const ControlsCell: FC<{
  data?: OnlineSourceListItem;
  canWrite?: boolean;
  onSelectAction: (
    action: { value: keyof typeof ONLINE_SOURCES_TABLE_ACTIONS },
    data: OnlineSourceListItem
  ) => void;
}> = ({ data, canWrite, onSelectAction, ...props }) => {
  const { t } = useTranslation();
  const options = useMemo(
    () =>
      [
        canWrite && {
          label: t('Delete'),
          value: ONLINE_SOURCES_TABLE_ACTIONS.delete,
        },
        canWrite &&
          (data?.is_active
            ? {
                label: t('Deactivate'),
                value: ONLINE_SOURCES_TABLE_ACTIONS.deactivate,
              }
            : {
                label: t('Activate'),
                value: ONLINE_SOURCES_TABLE_ACTIONS.activate,
              }),
        canWrite && {
          label: t('Settings'),
          value: ONLINE_SOURCES_TABLE_ACTIONS.settings,
        },
        canWrite &&
          data?.combined_status &&
          data.is_active &&
          (['pending', 'running'].includes(data.combined_status)
            ? {
                label: t('Cancel'),
                value: ONLINE_SOURCES_TABLE_ACTIONS.cancel,
              }
            : getCanStartCrawl(data) && {
                label: t('Start'),
                value: ONLINE_SOURCES_TABLE_ACTIONS.start,
              }),
      ].filter(Boolean),
    [canWrite, data, t]
  );
  return (
    <ActionCell
      title={t('Select Action')}
      menuType="absolute"
      options={options}
      onSelectAction={onSelectAction}
      {...props}
      data={data}
      width="15px"
      padding="15px"
    />
  );
};

const ExecutionStatusCell: FC<{
  status: OnlineSourceListItem['combined_status'];
}> = ({ status }) => {
  const { t } = useTranslation();
  const { label = EMPTY_VALUE, color = null } = useMemo(
    () =>
      status ? getExecutionStatusConfig(t)[status.toLowerCase()] || {} : {},
    [t, status]
  );
  return (
    <TextEllipsisWithTooltip color={color}>{label}</TextEllipsisWithTooltip>
  );
};

type UseColumnsProps = {
  onSelectAction: (
    action: { value: keyof typeof ONLINE_SOURCES_TABLE_ACTIONS },
    data: OnlineSourceListItem
  ) => void;
  canWrite?: boolean;
};

export const useColumns = ({ onSelectAction, canWrite }: UseColumnsProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { catalogId } = useParams<{ catalogId: string }>();
  return useMemo(
    () => [
      {
        id: 'spacer-start',
        headCell: <SizedCell width="15px" />,
        cell: <SizedCell width="15px" />,
        format: () => <span />,
      },
      {
        id: 'description',
        headCell: (
          <SortableHeadCell
            label={t('Description')}
            width="230px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="230px" padding="30px" />,
        format: (description: OnlineSourceListItem['description']) => {
          return (
            <TextEllipsisWithTooltip>
              {description || EMPTY_VALUE}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'base_url',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('URL')}
            width="230px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="230px" padding="30px" />,
        format: (baseUrl: OnlineSourceListItem['base_url']) => {
          return (
            <TextEllipsisWithTooltip>
              {baseUrl || EMPTY_VALUE}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'nb_pages',
        headCell: (
          <SortableHeadCell
            numeric
            label={t('# Pages')}
            width="80px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="80px" padding="30px" />,
        format: (pages: number, data: OnlineSourceListItem) => {
          const anchorProps = {
            type: 'anchor',
            href: `/knowledge/${catalogId}/online-sources/pages`,
            onClick: (e: MouseEvent<HTMLAnchorElement>) => {
              e.preventDefault();
              history.push({
                pathname: `/knowledge/${catalogId}/online-sources/pages`,
                state: {
                  source: data.id,
                },
              });
            },
          };
          return (
            <TextEllipsisWithTooltip {...anchorProps}>
              {pages || 0}
            </TextEllipsisWithTooltip>
          );
        },
      },
      {
        id: 'status',
        headCell: (
          <SortableHeadCell label={t('Status')} width="140px" padding="30px" />
        ),
        cell: <SizedCell width="140px" padding="30px" />,
        format: (_, data: OnlineSourceListItem) => {
          return <ExecutionStatusCell status={data.combined_status} />;
        },
      },
      {
        id: 'last_execution',
        headCell: (
          <SortableHeadCell
            numeric
            label={t('Last Run')}
            width="155px"
            padding="30px"
          />
        ),
        cell: <SizedCell width="155px" padding="30px" />,
        format: (_, data: OnlineSourceListItem) => {
          const lastRun = data.latest_execution?.started_at;
          return lastRun ? (
            <DateTimeInputInline readOnly value={lastRun} showDateTooltip />
          ) : (
            <KizenTypography>{EMPTY_VALUE}</KizenTypography>
          );
        },
      },
      {
        id: 'actions',
        headCell: (
          <SortableHeadCell
            hasSorting={false}
            label={t('Actions')}
            width="70px"
            padding="15px"
            align="right"
          />
        ),
        cell: <ControlsCell onSelectAction={onSelectAction} canWrite={canWrite} />,
      },
    ],
    [onSelectAction, canWrite, history, catalogId, t]
  );
};
